/**
 * @description Domain-aware drop-in replacement for wouter's <Link>.
 *
 * Behavior:
 * - On chiefintech.capital, fund hrefs ("/capital/fund/...") are transformed
 *   via fundHref() and rendered as a wouter <Link> (clean URL stays in the bar).
 * - On chiefintech.capital, every non-fund href ("/network/...", "/community",
 *   "/jobs", "/", etc.) is rendered as a plain <a> pointing to the correct
 *   sister domain (network → ewit.chiefintech.com, everything else →
 *   www.chiefintech.com) so the user crosses domains instead of 404-ing.
 * - On any other host (chiefintech.com, ewit.chiefintech.com, dev hosts),
 *   it behaves exactly like wouter's <Link>.
 *
 * API matches wouter's <Link> for the props we use across the app:
 *   href, children, className, onClick, data-testid.
 */
import { forwardRef } from "react";
import { Link as WouterLink } from "wouter";
import { fundHref, isCapitalDomain } from "@/lib/fundLinks";
import { getMainDomainUrl, getSubdomainUrl } from "@/lib/domainUtils";
import { isKnownCapitalAppPath } from "@shared/capitalSeo";

interface SmartLinkProps {
  href: string;
  children: React.ReactNode;
  className?: string;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  ["data-testid"]?: string;
  [key: string]: any;
}

const FUND_PREFIX = "/capital/fund";
const SHORT_PREFIX = "/fund";

function isFundPath(href: string): boolean {
  if (href === FUND_PREFIX || href.startsWith(FUND_PREFIX + "/") || href.startsWith(FUND_PREFIX + "?")) return true;
  if (href === SHORT_PREFIX || href.startsWith(SHORT_PREFIX + "/") || href.startsWith(SHORT_PREFIX + "?")) return true;
  return false;
}

/**
 * @description Returns an absolute cross-domain URL when on the capital
 * domain and the href targets a non-fund section. Returns null when the
 * href should remain a same-origin SPA link.
 */
function resolveCrossDomainHref(href: string): string | null {
  if (!isCapitalDomain()) return null;
  if (!href.startsWith("/")) return null; // external/anchor — leave alone
  if (isFundPath(href)) return null;
  const cleanPath = href.split("?")[0].split("#")[0];
  if (isKnownCapitalAppPath(cleanPath)) return null;
  if (href.startsWith("/network")) {
    return getSubdomainUrl("network", href);
  }
  return getMainDomainUrl(href);
}

export const SmartLink = forwardRef<HTMLAnchorElement, SmartLinkProps>(
  ({ href, children, ...rest }, ref) => {
    if (!href.startsWith("/")) {
      return (
        <a ref={ref} href={href} {...rest}>
          {children}
        </a>
      );
    }

    const crossDomain = resolveCrossDomainHref(href);

    if (crossDomain) {
      return (
        <a ref={ref} href={crossDomain} {...rest}>
          {children}
        </a>
      );
    }

    return (
      <WouterLink ref={ref} href={fundHref(href)} {...rest}>
        {children}
      </WouterLink>
    );
  }
);
SmartLink.displayName = "SmartLink";

export { SmartLink as Link };
